import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { t } from 'i18next';
import HeatmapModule from 'highcharts/modules/heatmap';
import ContentHeader from './contentHeader'
import changePositionSvg from "@/components/svg/changePosition.svg";
import {css} from "@emotion/react";
import {color} from "@/styles";

HeatmapModule(Highcharts);

interface Props {
  heatmapData: { data: number[][]; xCategories: string[]; yCategories: string[] };
  careRecordPlot: { x: number; y: number; color: string; name: string }[];
  notificationPlot: { x: number; y: number; color: string; name: string }[];
  positionChangedPlot: { x: number; y: number }[];
  showCareRecordPlot: boolean;
  showNotificationPlot: boolean;
  isPrinting?: boolean;
}

const LabelsContainer = css`
  display: flex;
  flex-direction: column;
  margin-left: 72px;
  margin-top: 8px;
`;

const Label = css`
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 100%;
  margin-right: 32px;

  color: ${color.dark_gray};
  & > span {
    margin-left: 8px;
  }
`;


const LabelRow = css`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const LargeSymbolScale = css`
  //  about 1.5 times of Label font size and follows 8 time px rule
  width: 16px;
  height: 16px;
`;

const ActSquare = css`
  width: 37px;
  height: 17px;
  background-color: #7FD856;
  display: inline-block;
  margin-right: 4px;
`;

const StaySquare = css`
  width: 37px;
  height: 17px;
  background-color: #E6E6E6;
  display: inline-block;
  margin-right: 4px;
`;


export const ActivityTrendHeatmapGraph = React.memo(
  ({
    heatmapData,
    careRecordPlot,
    notificationPlot,
    positionChangedPlot,
    showCareRecordPlot,
    showNotificationPlot,
    isPrinting = false,
  }: Props) => {

    const shiftPositionChangeIcon: number = 0.2;

    const shiftedPositionChangedPlot = positionChangedPlot.map((p) => ({x: p.x - shiftPositionChangeIcon, y: p.y}));

    // heatmapのセル右下に通知プロットをオフセット
    const notificationData = notificationPlot.map((r) => ({ ...r, x: r.x + 0.4, y: r.y - 0.3 }));

    // heatmapのセル右上にケア履歴プロットをオフセット
    const careRecordData = careRecordPlot.map((r) => ({ ...r, x: r.x + 0.4, y: r.y + 0.3 }));

    const xAxisMax = heatmapData.xCategories ? heatmapData.xCategories.length - 1 : 0;
    const yAxisMax = heatmapData.yCategories ? heatmapData.yCategories.length - 1 : 0;

    const option: Highcharts.Options = {
      title: {
        text: '',
      },
      chart: {
        zoomType: 'x',
        marginLeft: 72,
        marginRight: isPrinting ? 180 : 0,
        marginTop: 0,
        marginBottom: 56,
        shadow: false,
        animation: false,
        backgroundColor: '#FFFFFF',
        height: heatmapData.yCategories ? heatmapData.yCategories.length * 20 + 56 : 100,
      },
      xAxis: {
        categories: heatmapData.xCategories,
        min: 0,
        max: xAxisMax,
        tickInterval: 2,
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
      },
      yAxis: {
        reversed: false,
        categories: heatmapData.yCategories.map((date) => date.split('/').slice(1).join('/')),
        min: 0,
        max: yAxisMax,
        gridLineWidth: 0,
        title: { text: '' },
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
      },
      colorAxis: {
        min: 0,
        floor: 0,
        max: 1,
        stops: [
          [0, '#E6E6E6'],
          [1, '#A7D54C']
        ],
      },
      series: [
        {
          type: 'heatmap',
          data: heatmapData.data,
          colsize: 1,
          rowsize: 1,
          index: 0,
          dataLabels: {
            enabled: false  // 数値ラベルを非表示にする
          },
          tooltip: {
            headerFormat: '',
            pointFormatter: function () {
              const status = this.value === 1 ? t('act',  '動き有') : t('stay', '静止');
              return `<b>${status}</b>`;
            },
          },
        },
        {
          name: t('careHistory', 'ケア履歴'),
          data: careRecordData,
          type: 'scatter',
          yAxis: 0,
          index: 1,
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: '{point.name}<br />',
          },
          marker: {
            radius: 4,
            symbol: 'square',
          },
          stickyTracking: false,
          showInLegend: false,
          visible: showCareRecordPlot,
        },
        {
          name: t('notification', '通知'),
          data: notificationData,
          type: 'scatter',
          yAxis: 0,
          index: 1,
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: '{point.name}<br />',
          },
          marker: {
            radius: 4,
            symbol: `circle`,
          },
          stickyTracking: false,
          showInLegend: false,
          visible: showNotificationPlot,
        },
        {
          name: t('positionChanged', '姿勢変化'),
          data: shiftedPositionChangedPlot,
          type: 'scatter',
          yAxis: 0,
          index: 1,
          marker: {
            symbol: `url(${changePositionSvg})`,
            width: 20,
            height: 20
          },
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: '',
          },
          stickyTracking: false,
          showInLegend: false,
        }
      ],
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      boost: {
        useGPUTranslations: true,
      },
    };

    React.useEffect(() => {
      Highcharts.charts.forEach((chart) => {
        if (chart) {
          chart.reflow();
        }
      });
    }, [isPrinting]);

    const LabelIcon = ({ icon, label }: { icon: string; label: string }) => (
        <div css={Label}>
          <img src={icon} css={LargeSymbolScale} alt="" />
          <span>{label}</span>
        </div>
    );

    const TrendItem = ({ type }: { type: 'stay' | 'act';}) => {
      const cssStyle = type === 'stay' ? StaySquare : ActSquare;
      const label = type === 'act' ? t('act',  '動き有') : t('stay', '静止');

      return (
          <div css={Label}>
            <div css={cssStyle} />
            <span>{label}</span>
          </div>
      );
    };

    return (
      <div>
        <ContentHeader
            title={t('activityTrend', '活動傾向')}
            iconPath={'/images/residents/benTrendAnalysis/activityTrend.svg'}
            shiftLeft={isPrinting}
        />
        <HighchartsReact highcharts={Highcharts} options={option} />
        <div css={LabelsContainer}>
          <div css={LabelRow}>
            <LabelIcon
              icon={changePositionSvg}
              label={
                t('positionChanged', '姿勢変化')
                + ": "
                + t('whenPositionChangedWithin30Minutes', '前後30分で体勢が変わった時')
              }
            />
          </div>
          <div css={LabelRow}>
            <TrendItem type={'stay'} />
            <TrendItem type={'act'} />
          </div>
        </div>
      </div>
    );
  }
);
